'use client';

import type { CSSProperties, FC } from 'react';
import { useEffect } from 'react';
import { cn } from '../../../../utils/cn';
import { Credits } from '../../../../components/Credits';
import { SkinImage } from '../../../../components/ui/SkinImage';
import { useCountUp } from '../../../../hooks/useCountUp';
import { parseItemDetails } from '../../../../utils/parseItemDetails';
// WHY: SUPER_DROP_MS обязан совпадать с длительностью sdRoot в css
import { SUPER_DROP_MS } from '../../../../components/caseBattle/showTiming';
import './superDrop.css';

const SHARDS = Array.from({ length: 14 }, (_, i) => ({
  angle: Math.round((360 / 14) * i + (i % 2 ? 9 : -6)),
  dist: 120 + (i % 3) * 38,
  size: 5 + (i % 4) * 2,
  delay: 0.12 + (i % 5) * 0.03,
}));

export interface SuperDropItem {
  hashName: string;
  name: string;
  imageUrl: string | null;
  rarityColor: string;
  price: number;
}

interface SuperDropRevealProps {
  item: SuperDropItem;
  playerName?: string;
  title: string;
  position?: 'absolute' | 'fixed';
  onDone?: () => void;
}

export const SuperDropReveal: FC<SuperDropRevealProps> = ({ item, playerName, title, position = 'absolute', onDone }) => {
  const price = useCountUp(item.price);
  const parsed = parseItemDetails(item.hashName);

  useEffect(() => {
    if (!onDone) return;
    const timer = window.setTimeout(onDone, SUPER_DROP_MS);
    return () => window.clearTimeout(timer);
  }, [onDone]);

  const colorVars: CSSProperties = { '--sd-color': item.rarityColor };

  return (
    <div
      className={cn('sd-root pointer-events-none inset-0 z-30 flex flex-col items-center justify-center overflow-hidden', position === 'fixed' ? 'fixed' : 'absolute')}
      style={{
        ...colorVars,
        background: `radial-gradient(circle at 50% 45%, color-mix(in srgb, ${item.rarityColor} 28%, transparent), rgba(0,0,0,0.96) 62%)`,
        backdropFilter: 'blur(18px) saturate(0.6)',
        WebkitBackdropFilter: 'blur(18px) saturate(0.6)',
      }}
      aria-hidden
    >
      <span className="sd-flash absolute inset-0 bg-white" />
      <span className="sd-rays absolute left-1/2 top-[45%] size-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full" />

      {SHARDS.map((s, i) => (
        <span
          key={i}
          className="sd-shard"
          style={
            {
              '--sd-angle': `${s.angle}deg`,
              '--sd-dist': `${s.dist}px`,
              '--sd-shard-size': `${s.size}px`,
              '--sd-shard-delay': `${s.delay}s`,
              top: '45%',
            }
          }
        />
      ))}

      <div className="relative flex w-full flex-col items-center gap-3 px-6">
        <span className="sd-title text-[15px] font-black uppercase tracking-[4px] text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
          {title}
        </span>

        <div className="sd-item relative h-[220px] w-[300px]">
          <SkinImage name={item.hashName} src={item.imageUrl} className="h-full w-full" imgClassName="h-full w-full object-contain" priority />
        </div>

        <div className="sd-meta flex flex-col items-center gap-1 text-center">
          <span className="text-[13px] font-semibold uppercase tracking-[2px]" style={{ color: item.rarityColor }}>
            {parsed.weaponName || item.name}
          </span>
          <span className="max-w-[320px] truncate text-[26px] font-black leading-tight text-white">{parsed.skinName || item.name}</span>
          <span className="mt-1 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[15px] font-bold text-white">
            {playerName && (
              <>
                {playerName}
                <span className="opacity-40">·</span>
              </>
            )}
            <Credits amount={price} iconSize={14} className="tabular-nums" />
          </span>
        </div>
      </div>
    </div>
  );
};
